import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { FaStar, FaQuoteLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Testimonials = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: 'Rajesh & Family',
      location: 'Mumbai',
      rating: 5,
      text: 'Our 7-day Goa trip in the 9-seater caravan was absolutely amazing! The kids loved it, and we had all the comforts of home. The driver was professional and the caravan was spotlessly clean. Highly recommended!',
      trip: 'Goa Beach Route',
      avatar: '👨‍👩‍👧‍👦',
    },
    {
      name: 'Priya & Amit',
      location: 'Delhi',
      rating: 5,
      text: 'Perfect honeymoon experience! The 4-seater caravan gave us privacy and flexibility. Woke up to different scenic views each day. The team was very supportive throughout our Himalayan journey.',
      trip: 'Himalayan Route',
      avatar: '👫',
    },
    {
      name: 'Corporate Group',
      location: 'Pune',
      rating: 5,
      text: 'Booked for our team outing and it exceeded expectations! The caravan was well-equipped, entertainment system was great, and the overall experience brought our team closer. Will definitely book again!',
      trip: 'Rajasthan Circuit',
      avatar: '👥',
    },
    {
      name: 'Mehta Family',
      location: 'Nagpur',
      rating: 4,
      text: 'Excellent service from start to finish. The caravan had everything we needed - kitchen, bathroom, comfortable beds. Made our retirement trip memorable. Thank you WOW team!',
      trip: 'Wildlife Safari Tour',
      avatar: '👴👵',
    },
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" className="section-padding bg-white">
      <div className="container-custom">
        {/* Header */}
        <div
          ref={ref}
          className="text-center mb-16"
        >
          <span className="text-accent-600 font-semibold text-sm uppercase tracking-wide">
            Testimonials
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mt-4 mb-6">
            What Our Travelers
            <span className="text-accent-600"> Say About Us</span>
          </h2>
          <p className="text-xl text-primary-600 max-w-3xl mx-auto">
            Join hundreds of happy travelers who've experienced the joy of caravan holidays
          </p>
        </div>

        <div
          className="flex flex-col md:flex-row items-center justify-center gap-8 mb-16"
        >
          <div className="text-center">
            <div className="text-6xl font-bold text-accent-500 mb-2">4.5</div>
            <div className="flex text-accent-500 text-2xl mb-2 justify-center">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className={i < 4 ? '' : 'opacity-30'} />
              ))}
            </div>
            <p className="text-primary-600">Average Rating</p>
          </div>
          <div className="h-20 w-px bg-primary-200 hidden md:block" />
          <div className="text-center">
            <div className="text-6xl font-bold text-forest-500 mb-2">500+</div>
            <p className="text-primary-600 text-lg">Happy Travelers</p>
          </div>
          <div className="h-20 w-px bg-primary-200 hidden md:block" />
          <div className="text-center">
            <div className="text-6xl font-bold text-primary-600 mb-2">60+</div>
            <p className="text-primary-600 text-lg">Reviews on Justdial</p>
          </div>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-3xl p-12 shadow-2xl relative">
            {/* Quote Icon */}
            <div className="absolute top-8 left-8 text-6xl text-accent-200">
              <FaQuoteLeft />
            </div>

            <div className="relative z-10">
              {/* Avatar & Info */}
              <div className="flex items-center mb-6">
                <div className="text-6xl mr-4">
                  {testimonials[currentIndex].avatar}
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-primary-800">
                    {testimonials[currentIndex].name}
                  </h4>
                  <p className="text-primary-600">
                    {testimonials[currentIndex].location}
                  </p>
                  <div className="flex text-accent-500 mt-1">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <FaStar key={i} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Testimonial Text */}
              <p className="text-xl text-primary-700 leading-relaxed mb-6 italic">
                "{testimonials[currentIndex].text}"
              </p>

              {/* Trip Info */}
              <div className="inline-block bg-accent-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                Trip: {testimonials[currentIndex].trip}
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 bg-primary-700 text-white rounded-full flex items-center justify-center hover:bg-primary-800 shadow-lg"
            >
              <FaChevronLeft />
            </button>
            
            {/* Dots */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-3 rounded-full transition-all ${
                    index === currentIndex
                      ? 'w-8 bg-accent-500'
                      : 'w-3 bg-primary-300 hover:bg-primary-400'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="w-12 h-12 bg-primary-700 text-white rounded-full flex items-center justify-center hover:bg-primary-800 shadow-lg"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div
          className="mt-16 text-center"
        >
          <p className="text-primary-600 mb-4">
            Find us on trusted platforms
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <span className="px-6 py-3 bg-white rounded-full shadow-md text-primary-700 font-semibold">
              Justdial 4.1★
            </span>
            <span className="px-6 py-3 bg-white rounded-full shadow-md text-primary-700 font-semibold">
              Instagram @wowcaravans
            </span>
            <span className="px-6 py-3 bg-white rounded-full shadow-md text-primary-700 font-semibold">
              Facebook Reviews
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
